/* eslint-disable @typescript-eslint/no-explicit-any */
import { ActionCreatorWithoutPayload, ActionCreatorWithPayload } from '@reduxjs/toolkit';
import { skipUntil } from 'rxjs';
import { debounceTime, filter, map, take, withLatestFrom } from 'rxjs/operators';
import { TableActions, TableSettings } from '..';
import { fetchUserSettings, updateTableSettings } from '../../../features/settings/redux/settingsSlices';
import { RootEpic, RootState } from '../../../store';

export const createSaveTableSettingsEpic =
    (
        tableActions: TableActions,
        tableSelector: (state: RootState) => TableSettings,
        additionalActionTriggers?: (ActionCreatorWithPayload<any> | ActionCreatorWithoutPayload)[]
    ): RootEpic =>
    (action$, state$) => {
        const actions: (ActionCreatorWithPayload<any> | ActionCreatorWithoutPayload)[] = [
            tableActions.columnsSelected,
            tableActions.columnsReordered,
            tableActions.sortingChanged,
            tableActions.filtersChanged,
            tableActions.columnsResized,
            tableActions.showTooltipsChanged,
            ...(additionalActionTriggers ?? [])
        ];

        return action$.pipe(
            filter(a => actions.some(ta => ta.match(a))),
            skipUntil(action$.pipe(filter(fetchUserSettings.success.match), take(1))),
            debounceTime(500),
            withLatestFrom(state$.pipe(map(state => tableSelector(state)))),
            map(([, tableSettings]) => {
                if (tableSettings.table == null) {
                    throw new Error('The TableType must be set when saving table settings!');
                }

                return updateTableSettings.request({
                    ascSort: tableSettings.sorting.direction === 'asc',
                    table: tableSettings.table,
                    showTooltips: tableSettings.showTooltips,
                    tableColumnSettings: tableSettings.columns.map((c, i) => ({
                        isSortedOn: c.key === tableSettings.sorting.column,
                        isVisible: !c.hide,
                        name: c.key,
                        order: i,
                        filter: tableSettings.filters[c.key],
                        width: Number(c.width?.toFixed(0)) // Remove decimals
                    }))
                });
            })
        );
    };
